// frontend/app/components/lessonPlans/lessonMatch.js
//
// Ranks the lesson list a teacher already has in memory against the words they
// typed. No React, no network — like lessonPlanRules.js, this is the piece
// worth unit-testing, so it deliberately has no other dependencies.
//
// WHY THIS IS LOCAL. The whole board's lesson list is loaded up front
// (AITutorApp.js `apiListLessonPlans`), so ranking it costs a loop over a few
// dozen rows. Sending each keystroke to a server would put a round-trip between
// a teacher and a list they are already holding.
//
// WHAT THIS IS NOT. This answers "your words appear in these lessons", which is
// a different question from the one backend/services/lesson_match.py answers —
// "Saras thinks you mean this". The two are allowed to disagree, and the UI
// labels them differently for exactly that reason. This module never guesses:
// every point it awards can be traced back to a word the teacher typed, which
// is why `terms` is returned alongside the score.
//
// WHAT THE QUALIFICATION RULE DOES NOT CATCH — weighed and declined, not missed.
//
// `rankLessons` drops a lesson whose only hits landed in the supporting prose
// unless the query is fully covered. That kills the noisiest case (a word shared
// by a whole chapter heading). It does not kill a GENERIC word sitting in an
// identifying field. The worked example, from the seeded library:
//
//   "quadratic equations" -> "Making Food from Air, Water and Light", score 3.00
//
// because "equation" appears in that lesson's objective "Write the word equation
// for photosynthesis and name its products." Half the query, in an identifying
// field, so it qualifies — and because a thin match is still a match, it also
// denies the AI fallback its turn.
//
// Demoting `objectives` to prose would drop it, and would also drop "i want to
// teach the heart" -> "Overview of the Circulatory System", where "heart" is
// likewise in the objectives and nowhere in the title. That is a real query
// silently returning nothing, traded for one cosmetically thin row. What
// actually separates the two cases is that "quadratic" is absent from the
// library while "equations" is generic, whereas "heart" is discriminating and
// present — that is inverse document frequency, not another field tier, and any
// tier rule sharp enough to catch the first also catches the second. L1's Python
// ranker has IDF; this one deliberately does not.
//
// The residue is bounded and visible: 3.00 against 12-18 for genuine matches,
// with "Matched: equations" printed under the row and Show all lessons beside
// it. A teacher can see that match is thin. Add IDF here if that stops being
// true — do not reach for another tier.

/**
 * Words that appear in almost every lesson and in almost every description of
 * one, so scoring them separates nothing. Deliberately short: an aggressive
 * list would eat "how", "why" and "make", and "how do plants make food" is
 * precisely the sentence this box exists to answer.
 */
export const STOPWORDS = new Set([
  "a", "an", "the", "and", "or", "but", "if", "of", "on", "in", "at", "to",
  "for", "from", "with", "into", "onto", "about", "as", "by", "is", "are",
  "was", "were", "be", "been", "being", "do", "does", "did", "i", "we", "you",
  "they", "it", "its", "this", "that", "these", "those", "my", "our", "their",
  "want", "need", "like", "would", "should", "could", "can", "will", "please",
  // Said of every lesson in the library, so they carry no discrimination here.
  "lesson", "lessons", "class", "classes", "teach", "teaching", "topic",
  "something", "anything",
]);

/**
 * How much a term is worth in each field, and why:
 *
 *  - `title` is what a teacher would call the lesson out loud, so a hit there
 *    is the strongest evidence there is.
 *  - `objectives` matter as much as the syllabus label because teachers
 *    describe the outcome ("students can explain why leaves are green"), not
 *    the chapter heading.
 *  - `description` / `subtitle` are prose written about this one lesson.
 *  - `chapter_name` / `subject_name` are shared by dozens of lessons — "biology"
 *    matches half the board, so a hit there orders almost nothing.
 *  - `prerequisites` name what comes BEFORE this lesson. Matching there is real
 *    signal (the teacher is in the right neighbourhood) but it is evidence for
 *    a different lesson than the one they described.
 *  - `difficulty` is one word, so typing "foundation" narrows the list without
 *    outranking anything that matched on meaning.
 */
export const FIELD_WEIGHTS = {
  title: 6,
  topic_name: 4,
  objectives: 4,
  description: 3,
  subtitle: 3,
  chapter_name: 2,
  subject_name: 2,
  prerequisites: 2,
  difficulty: 1,
};

/**
 * The fields that say what a lesson IS, as opposed to the prose around it.
 *
 * A hit here qualifies a lesson on its own. A hit only in the supporting fields
 * qualifies it only when the query is fully covered — see `rankLessons`.
 */
export const IDENTIFYING_FIELDS = ["title", "topic_name", "objectives"];

/** A term this long may match on its prefix; below it, "car" would hit "carbon". */
const MIN_PREFIX = 4;

/** A prefix hit is half a whole-word hit — it is a guess about a word in flight. */
const PARTIAL_WEIGHT = 0.5;

/** A pasted paragraph must not turn one keystroke into thousands of comparisons. */
const MAX_QUERY_TERMS = 24;

/**
 * Fold every shape a lesson field arrives in — string, array of strings, array
 * of `{ text }` — down to one string. `prerequisites` in particular has been
 * both a list and a paragraph depending on who seeded the lesson.
 */
const textOf = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(textOf).join(" ");
  if (typeof value === "object") return textOf(value.text ?? value.label ?? "");
  return String(value);
};

/**
 * Crude but predictable singularisation: "leaves" and "leaf" stay two words,
 * but "cells"/"cell" and "properties"/"property" fold together, which covers
 * what teachers actually type. A real stemmer would be a dependency and a
 * source of surprises.
 *
 * The `is`/`us`/`ss` guard is not an edge case in this vocabulary — without it
 * "photosynthesis" becomes "photosynthesi", "nucleus" becomes "nucleu" and
 * "analysis" becomes "analysi". Every word this box exists to match is a
 * science noun, and a third of them end in one of those.
 */
export const singular = (word) => {
  if (word.length <= 3) return word;
  if (word.endsWith("ies")) return `${word.slice(0, -3)}y`;
  if (/(ss|sh|ch|x|z)es$/.test(word)) return word.slice(0, -2);
  if (/(is|us|ss)$/.test(word) || !word.endsWith("s")) return word;
  return word.slice(0, -1);
};

/**
 * Text -> `[{ term, word }]`: what to match on, and the word it was written as.
 *
 * Both are kept because they are read by different audiences. `term` is the
 * folded form the scorer compares; `word` is what the teacher typed, and it is
 * what the dropdown shows back to them — telling someone their search matched
 * "leave" when they typed "leaves" makes the explanation look like a bug.
 *
 * Order is preserved and duplicates dropped, so the matched terms read back in
 * the teacher's own phrasing rather than in field order.
 */
const termsOf = (value) => {
  const words = textOf(value)
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);

  const seen = new Set();
  const terms = [];
  words.forEach((word) => {
    const term = singular(word);
    if (term.length < 2 || STOPWORDS.has(word) || STOPWORDS.has(term)) return;
    if (seen.has(term)) return;
    seen.add(term);
    terms.push({ term, word });
  });
  return terms;
};

/** The folded terms of any text or field, for matching. */
export const tokenize = (value) => termsOf(value).map((entry) => entry.term);

/** The scorable fields of one lesson, tokenised once per lesson per query. */
const lessonTerms = (lesson) => {
  const byField = {};
  Object.keys(FIELD_WEIGHTS).forEach((field) => {
    byField[field] = new Set(tokenize(lesson?.[field]));
  });
  return byField;
};

/**
 * What one field is worth for one term: its full weight for the word itself,
 * half for a word that merely starts with it. Prefix matching is what makes the
 * list move usefully while a teacher is still typing "photosynth".
 */
const fieldScore = (terms, term, weight) => {
  if (terms.has(term)) return weight;
  if (term.length < MIN_PREFIX) return 0;
  for (const candidate of terms) {
    if (candidate.length > term.length && candidate.startsWith(term)) {
      return weight * PARTIAL_WEIGHT;
    }
  }
  return 0;
};

/**
 * Rank lessons against a free-text description, best first.
 *
 * Returns `[{ lesson, score, terms }]` where `terms` are the words from the
 * query this lesson actually answered — the dropdown shows them, because a
 * ranked list with no visible reason is indistinguishable from a guess.
 *
 * A lesson matching nothing is omitted entirely rather than ranked last: an
 * empty result is what turns the AI fallback on, and burying a non-match at
 * position nine would hide the fact that the words found nothing.
 *
 * Never mutates `lessons`.
 */
export const rankLessons = (lessons, query, { limit = 0 } = {}) => {
  const queryTerms = termsOf(query).slice(0, MAX_QUERY_TERMS);
  const list = Array.isArray(lessons) ? lessons : [];
  if (queryTerms.length === 0 || list.length === 0) return [];

  const ranked = [];

  list.forEach((lesson) => {
    const fields = lessonTerms(lesson);
    const matched = [];
    let total = 0;
    // Did any word land in a field that says what this lesson is about?
    let identified = false;

    queryTerms.forEach(({ term, word }) => {
      // The BEST field a term appears in, not the sum of all of them.
      //
      // Summing looked reasonable and ranked the real library wrong: for "how
      // blood gets round the body", a lesson on hormones collected "blood" and
      // "body" across its objectives, description and prerequisites (4+3+2) and
      // beat the lesson whose title is Blood Vessels. Three incidental mentions
      // must not out-argue one deliberate one — that is what the weights mean.
      let termScore = 0;
      Object.entries(FIELD_WEIGHTS).forEach(([field, weight]) => {
        const hit = fieldScore(fields[field], term, weight);
        if (hit > 0 && IDENTIFYING_FIELDS.includes(field)) identified = true;
        termScore = Math.max(termScore, hit);
      });
      if (termScore > 0) {
        matched.push(word);
        total += termScore;
      }
    });

    if (total <= 0) return;

    // A lesson that answers every word the teacher typed should outrank one
    // that answers a single word very loudly. Half the score is carried by the
    // hits themselves and half by how much of the sentence they covered, so
    // coverage tilts close calls without letting one shared word beat a title.
    const coverage = matched.length / queryTerms.length;

    // A word that landed only in the supporting prose is not a match unless it
    // accounts for the whole query. "quadratic equations" found "equation" in
    // the description of a photosynthesis lesson — one word of two, in prose —
    // and a confident-looking row for the wrong subject is worse than no row:
    // a thin match is still a match, so it silently denies the AI fallback its
    // turn, and Saras never gets to say "nothing here covers that".
    //
    // Deliberately a rule about WHERE the words landed rather than a score
    // threshold. There is no confidence to tune, and it explains itself to a
    // teacher in one sentence: your words have to appear in what the lesson is
    // about, or else in all of what you typed.
    if (!identified && coverage < 1) return;

    const score = total * (0.5 + 0.5 * coverage);

    ranked.push({
      lesson,
      // Rounded so a snapshot of the ranking is stable and comparable; the
      // ordering never depends on the fourteenth decimal place.
      score: Math.round(score * 1000) / 1000,
      terms: matched,
    });
  });

  // Ties break on lesson number, then id, so the same query always produces the
  // same list — a teacher scanning for the second time must not find it shuffled.
  ranked.sort(
    (a, b) =>
      b.score - a.score ||
      (Number(a.lesson?.lesson_no) || 0) - (Number(b.lesson?.lesson_no) || 0) ||
      String(a.lesson?.id).localeCompare(String(b.lesson?.id))
  );

  return limit > 0 ? ranked.slice(0, limit) : ranked;
};

/**
 * What to offer before anyone has typed anything.
 *
 * Sorted by `updated_at` when the payload carries it, and left in the server's
 * own order otherwise — which is lesson order, and reading Lesson 1, 2, 3 on a
 * freshly seeded board is better than reading them shuffled by a field that is
 * missing from every row.
 *
 * Never mutates `lessons`.
 */
export const recentLessons = (lessons, limit = 0) => {
  const list = Array.isArray(lessons) ? [...lessons] : [];
  const dated = list.some((lesson) => lesson?.updated_at);

  if (dated) {
    list.sort((a, b) =>
      String(b?.updated_at || "").localeCompare(String(a?.updated_at || ""))
    );
  }

  return limit > 0 ? list.slice(0, limit) : list;
};

export default rankLessons;
